import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useQuery } from '@apollo/client';
import PropTypes from 'prop-types';

import { ErrorHandler } from '@playbookux/playbook-web-core';
import TreeComponent from '../../../core/components/tree-component';
import FollowUpQuestions from './follow-up-questions';

import {
  clearQuestions,
  setAnswers,
  setNextQuestion,
  setQuestions,
} from '../../../core/actions';

import { PROJECT_WITH_TREE_TEST_AND_SURVEY } from '../../../core/graphql/tree-test';
import { GET_TEST_SESSION_STATUS } from '../../../core/graphql/test-sessions';

import {
  Button,
  Container,
  ContainerContent,
  ContainerTree,
  Divider,
  Loader,
  Text,
  Title,
} from './styles';
import { QUESTION_TYPE } from './constants';
import { handleRedirectSession } from '../../utils/handleRedirectSession';
import { Message } from '../recruit-participants/styles';

const TreeTestTask = ({
  history,
  onClearQuestions,
  onSetAnswers,
  onSetQuestions,
  treeTest,
}) => {
  const {
    currentQuestion = {},
    error: treeError,
    maxTasks = 1,
    project: projectReducer = {},
    questionIndex = 1,
    questions = [],
    testSession = {},
  } = treeTest;

  const [errorField, setErrorField] = useState(false);
  const [showTreeTest, setShowTreeTest] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const testSessionId = testSession?.id;
  const projectId = projectReducer?.id;

  const handleShowTreeTest = () => {
    setShowTreeTest(true);
  };

  const {
    data = {},
    error,
    loading,
  } = useQuery(PROJECT_WITH_TREE_TEST_AND_SURVEY, {
    variables: {
      id: projectId,
    },
    skip: _.isEmpty(projectId),
  });

  const {
    data: dataTestSession = {},
    loading: loadingTestSessionStatus,
  } = useQuery(GET_TEST_SESSION_STATUS, {
    variables: {
      testSessionId,
    },
    skip: _.isEmpty(testSessionId),
  });

  const { testSessions = {} } = dataTestSession;
  const { project = {} } = data;
  const {
    thankYouRedirectLink = '',
    thankYouTitle = '',
    thankYouContent = '',
    treeTestNodes = '',
    hasMrId,
  } = project;

  const handleCompleteSession = () => {
    if (!thankYouRedirectLink) {
      history.push({
        pathname: '/thank-you',
        state: {
          title: thankYouTitle,
          content: thankYouContent,
        },
      });
    } else {
      const testSessionMrId = testSessions[0]?.tester?.mrId;
      handleRedirectSession(thankYouRedirectLink, hasMrId, testSessionMrId);
    }

    onClearQuestions();
  };

  /**
   * Call the function to save the tree path and the follow-up-questions.
   * Go to the next Task or Question.
   */
  const handleTesterSubmit = async (values) => {
    setIsLoading(true);

    if (_.isEmpty(values)) {
      setErrorField(true);
    } else {
      setErrorField(false);
      const { sessionCompleted } = await onSetAnswers(values);

      if (sessionCompleted) {
        handleCompleteSession();
      }
    }

    setIsLoading(false);
  };

  // Reset tree if question was changed on tester's panel
  useEffect(() => {
    if (!_.isEmpty(currentQuestion) && currentQuestion.type === QUESTION_TYPE.TASK) {
      setErrorField(false);
      setShowTreeTest(false);
      setIsLoading(false);
    }
  }, [currentQuestion]);

  // Set the initial question values on redux.
  useEffect(() => {
    if (!loading && _.isEmpty(questions)) {
      const sortRandom = _.get(project, 'sortRandom', false);

      const surveys = _.get(project, 'surveys', []);
      const surveySession = _.filter(surveys, (v) => v.surveyType === 'session');
      const firstSurveys = _.head(surveySession);
      const questionsSurvey = _.get(firstSurveys, 'questions', []);

      onSetQuestions(questionsSurvey, 'questionOrder', sortRandom);
    }
  }, [currentQuestion, loading, onSetQuestions, project, questions]);

  if (!_.isEmpty(testSessions) && _.head(testSessions).status === 'processed') {
    return (
      <Container>
        <Text>
          All the questions have been answered!
        </Text>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <ErrorHandler
          error={error}
          defaultMessage="Something went wrong."
        />
      </Container>
    );
  }

  if (testSessions[0] && new Date(testSessions[0].expiresAt) < new Date()) {
    return (
      <Container center>
        <Message>Test session expired</Message>
      </Container>
    );
  }

  const queriesLoading = (loading || loadingTestSessionStatus);

  return (
    <>
      {currentQuestion.type === QUESTION_TYPE.TASK && (
        <Container>
          <ContainerContent>
            <Text>{`Task ${questionIndex} of ${maxTasks}`}</Text>
            <Title>{currentQuestion?.label}</Title>

            <Divider />

            {(queriesLoading) && (<Loader data-loader="tree-test-loader" />)}

            {!queriesLoading && !showTreeTest && (
              <Button
                loading={queriesLoading}
                onClick={handleShowTreeTest}
              >
                Start this task
              </Button>
            )}

            {showTreeTest && (
              <ContainerTree>
                <TreeComponent
                  isTester
                  loading={isLoading}
                  onSubmit={handleTesterSubmit}
                  treeData={treeTestNodes}
                />
              </ContainerTree>
            )}
          </ContainerContent>
        </Container>
      )}

      {currentQuestion.type === QUESTION_TYPE.FOLLOW_UP_QUESTION && (
        <FollowUpQuestions
          errorField={errorField}
          errorRetry={treeError}
          loading={isLoading}
          onSubmit={handleTesterSubmit}
          question={currentQuestion}
        />
      )}

      {treeError && (
        <Container>
          <Text>
            Something went wrong while saving the answers. Try again.
          </Text>
        </Container>
      )}
    </>
  );
};

TreeTestTask.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  location: PropTypes.shape({
    search: PropTypes.string,
    pathname: PropTypes.string,
  }).isRequired,
  onClearQuestions: PropTypes.func,
  onSetAnswers: PropTypes.func,
  onSetQuestions: PropTypes.func,
  treeTest: PropTypes.shape({
    answers: PropTypes.arrayOf(PropTypes.shape({})),
    currentQuestion: PropTypes.shape({}),
    isLastQuestion: PropTypes.bool,
    isRandom: PropTypes.bool,
    maxTasks: PropTypes.number,
    error: PropTypes.shape({
      message: PropTypes.string,
    }),
    nextQuestion: PropTypes.shape({}),
    questionIndex: PropTypes.number,
    questions: PropTypes.arrayOf(PropTypes.shape({})),
    project: PropTypes.shape({
      id: PropTypes.string,
    }),
    testSession: PropTypes.shape({
      id: PropTypes.string,
    }),
  }),
};

TreeTestTask.defaultProps = {
  onClearQuestions: () => { },
  onSetAnswers: () => { },
  onSetQuestions: () => { },
  treeTest: {
    currentQuestion: {},
    nextQuestion: {},
    questions: [],
    isLastQuestion: false,
    answers: {},
    questionIndex: 1,
    isRandom: false,
    project: {},
    maxTasks: 1,
    testSession: {},
  },
};

const mapStateToProps = ({ treeTest }) => ({
  treeTest,
});

const mapDispatchToProps = {
  onClearQuestions: clearQuestions,
  onSetAnswers: setAnswers,
  onSetNextQuestion: setNextQuestion,
  onSetQuestions: setQuestions,
};

const enhance = compose(
  connect(mapStateToProps, mapDispatchToProps),
);

export default enhance(TreeTestTask);
